import React from 'react';

// Paginator.jsx
// Lightweight controlled paginator that mimics the behavior/API surface of
// react-bootstrap-table2-paginator's default UI, but reports changes using
// the same event shape LabelList (and siblings) already expect:
//   onTableChange('pagination', { page, sizePerPage })
//
// It is driven entirely by props (pageIndex, pageSize, totalSize) coming
// from Redux; it holds no internal page state, matching the `remote` table
// behavior used across the app.

const DEFAULT_SIZE_PER_PAGE_LIST = [10, 25, 50, 100];

const getCurrentPage = (pageIndex, pageSize) => {
    if (!pageSize) return 1;
    return Math.floor(pageIndex / pageSize) + 1;
};

const Paginator = ({
    pageIndex = 0,
    pageSize = 10,
    totalSize = 0,
    onTableChange,
    sizePerPageList = DEFAULT_SIZE_PER_PAGE_LIST,
}) => {
    const currentPage = getCurrentPage(pageIndex, pageSize);
    const totalPages = Math.max(1, Math.ceil((totalSize || 0) / (pageSize || 1)));

    const emitChange = (page, sizePerPage) => {
        if (typeof onTableChange === 'function') {
            onTableChange('pagination', { page, sizePerPage });
        }
    };

    const goToPage = (page) => {
        const safePage = Math.min(Math.max(1, page), totalPages);
        if (safePage === currentPage) return;
        emitChange(safePage, pageSize);
    };

    const changeSizePerPage = (event) => {
        const newSizePerPage = parseInt(event.target.value, 10);
        emitChange(1, newSizePerPage);
    };

    if (!totalSize) {
        return null;
    }

    // Build a small window of page numbers around the current page to avoid
    // rendering hundreds of <li> for large datasets.
    const windowSize = 5;
    const start = Math.max(1, currentPage - Math.floor(windowSize / 2));
    const end = Math.min(totalPages, start + windowSize - 1);
    const pages = [];
    for (let p = start; p <= end; p++) pages.push(p);

    return (
        <div className="d-flex justify-content-between align-items-center flex-wrap table-adapter-paginator">
            <div className="table-adapter-paginator__size">
                <select
                    className="form-control form-control-sm d-inline-block w-auto"
                    value={pageSize}
                    onChange={changeSizePerPage}
                >
                    {sizePerPageList.map(size => (
                        <option key={size} value={size}>{size}</option>
                    ))}
                </select>
            </div>

            <nav>
                <ul className="pagination pagination-sm mb-0">
                    <li className={`page-item ${currentPage <= 1 ? 'disabled' : ''}`}>
                        <button type="button" className="page-link" onClick={() => goToPage(currentPage - 1)}>
                            «
                        </button>
                    </li>
                    {pages.map(p => (
                        <li key={p} className={`page-item ${p === currentPage ? 'active' : ''}`}>
                            <button type="button" className="page-link" onClick={() => goToPage(p)}>
                                {p}
                            </button>
                        </li>
                    ))}
                    <li className={`page-item ${currentPage >= totalPages ? 'disabled' : ''}`}>
                        <button type="button" className="page-link" onClick={() => goToPage(currentPage + 1)}>
                            »
                        </button>
                    </li>
                </ul>
            </nav>
        </div>
    );
};

export default Paginator;
